import { useMemo, useState, type ReactNode } from "react";
import { ChevronDown, ChevronUp, ChevronsUpDown } from "lucide-react";
import { Skeleton } from "./ScreenStates";
import { compareSortValues, type GridSort, type GridSortValue } from "./gridSort";

export type { GridSort, GridSortValue };

export interface GridColumn<Row> {
  key: string;
  /** Header text; a "\n" breaks it onto two lines to keep narrow columns narrow. */
  header: string;
  align?: "left" | "right" | "center";
  render: (row: Row) => ReactNode;
  /** Enables sorting when provided. */
  sortValue?: (row: Row) => GridSortValue;
  widthClassName?: string;
  /** Visually hide the header text while keeping it for screen readers. */
  hideHeader?: boolean;
}


export type GridDensity = "default" | "compact";

interface DataGridProps<Row> {
  caption: string;
  columns: readonly GridColumn<Row>[];
  rows: readonly Row[];
  rowKey: (row: Row) => string;
  isLoading?: boolean;
  skeletonRows?: number;
  emptyState?: ReactNode;
  defaultSort?: GridSort;
  /** Replaces the flat sort, for grids whose rows must stay grouped (dependents under their subscriber). */
  sortRows?: (rows: readonly Row[], sort: GridSort, sortValue: (row: Row) => GridSortValue) => Row[];
  /** Compact rows fit more members per screen (36px rows, 13px text). */
  density?: GridDensity;
  rowDepth?: (row: Row) => number;
  isRowExcluded?: (row: Row) => boolean;
  rowClassName?: (row: Row) => string;
  renderActions?: (row: Row) => ReactNode;
  actionsHeader?: string;
  children?: never;
}

const ALIGN: Record<NonNullable<GridColumn<unknown>["align"]>, string> = { left: "text-left", right: "text-right tabular-nums", center: "text-center" };

const DENSITY = {
  default: { row: "h-11", cell: "px-3 py-1.5 text-sm", head: "px-3 py-3" },
  compact: { row: "h-9", cell: "px-2 py-1 text-[13px]", head: "px-2 py-2 leading-tight" },
} as const;


function SortIcon({ active, direction }: { active: boolean; direction: "asc" | "desc" }) {
  if (!active) return <ChevronsUpDown size={14} className="text-gray-300" aria-hidden="true" />;
  return direction === "asc" ? <ChevronUp size={14} aria-hidden="true" /> : <ChevronDown size={14} aria-hidden="true" />;
}

function HeaderText({ header, hidden }: { header: string; hidden?: boolean }) {
  const lines = header.split("\n");
  return (
    <span className={hidden ? "sr-only" : "inline-block"}>
      {lines.map((line, index) => (
        <span key={line} className="block">
          {line}
          {index < lines.length - 1 && <span className="sr-only"> </span>}
        </span>
      ))}
    </span>
  );
}

/** Typed table with sticky header, sort, tree indent, excluded styling, skeleton mode, density, and a trailing actions column. */
export function DataGrid<Row>({ caption, columns, rows, rowKey, isLoading = false, skeletonRows = 6, emptyState, defaultSort, sortRows, density = "default", rowDepth, isRowExcluded, rowClassName, renderActions, actionsHeader = "Actions" }: DataGridProps<Row>) {
  const [sort, setSort] = useState<GridSort | null>(defaultSort ?? null);
  const sizing = DENSITY[density];

  const sortedRows = useMemo(() => {
    if (!sort) return rows;
    const column = columns.find((candidate) => candidate.key === sort.key);
    if (!column?.sortValue) return rows;
    const sortValue = column.sortValue;
    if (sortRows) return sortRows(rows, sort, sortValue);
    const factor = sort.direction === "asc" ? 1 : -1;
    return [...rows].sort((a, b) => compareSortValues(sortValue(a), sortValue(b)) * factor);
  }, [rows, sort, columns, sortRows]);

  const handleSort = (key: string) => {
    setSort((current) => (current?.key === key ? { key, direction: current.direction === "asc" ? "desc" : "asc" } : { key, direction: "asc" }));
  };

  if (!isLoading && rows.length === 0 && emptyState) return <>{emptyState}</>;

  return (
    <div className="rounded-2xl bg-white shadow-md max-xl:overflow-x-auto">
      <table className="w-full border-collapse" aria-busy={isLoading || undefined}>
        <caption className="sr-only">{caption}</caption>
        <thead className="sticky top-16 z-10 bg-gray-50 text-xs font-semibold uppercase tracking-wide text-gray-600">
          <tr>
            {columns.map((column) => {
              const isSorted = sort?.key === column.key;
              const ariaSort = isSorted ? (sort.direction === "asc" ? "ascending" : "descending") : undefined;
              return (
                <th key={column.key} scope="col" aria-sort={column.sortValue ? (ariaSort ?? "none") : undefined} className={`border-b border-gray-200 align-bottom ${sizing.head} ${ALIGN[column.align ?? "left"]} ${column.widthClassName ?? ""}`}>
                  {column.sortValue ? (
                    <button type="button" onClick={() => handleSort(column.key)} className="inline-flex cursor-pointer items-end gap-1 rounded text-left focus:outline-none focus:ring-2 focus:ring-brand-green-50">
                      <HeaderText header={column.header} hidden={column.hideHeader} />
                      <SortIcon active={isSorted} direction={sort?.direction ?? "asc"} />
                    </button>
                  ) : (
                    <HeaderText header={column.header} hidden={column.hideHeader} />
                  )}
                </th>
              );
            })}
            {renderActions && (
              <th scope="col" className={`border-b border-gray-200 text-right ${sizing.head}`}>
                <span className="sr-only">{actionsHeader}</span>
              </th>
            )}
          </tr>
        </thead>
        <tbody>
          {isLoading && rows.length === 0
            ? Array.from({ length: skeletonRows }, (_, index) => (
                <tr key={`skeleton-${index}`} className={`${sizing.row} border-b border-gray-100`}>
                  {columns.map((column) => (
                    <td key={column.key} className={sizing.cell}>
                      <Skeleton className="h-4 w-full" />
                    </td>
                  ))}
                  {renderActions && <td className={sizing.cell} />}
                </tr>
              ))
            : sortedRows.map((row) => {
                const excluded = isRowExcluded?.(row) ?? false;
                const depth = rowDepth?.(row) ?? 0;
                return (
                  <tr key={rowKey(row)} className={`${sizing.row} border-b border-gray-100 hover:bg-gray-50 ${excluded ? "opacity-50" : ""} ${rowClassName?.(row) ?? ""}`} aria-label={excluded ? "Excluded" : undefined}>
                    {columns.map((column, columnIndex) => (
                      <td key={column.key} className={`${sizing.cell} ${ALIGN[column.align ?? "left"]} ${column.widthClassName ?? ""} ${excluded && columnIndex === 0 ? "line-through" : ""}`} style={columnIndex === 0 && depth > 0 ? { paddingLeft: `${0.5 + depth * 1.25}rem` } : undefined}>
                        {column.render(row)}
                      </td>
                    ))}
                    {renderActions && <td className="whitespace-nowrap px-1 py-0.5 text-right">{renderActions(row)}</td>}
                  </tr>
                );
              })}
        </tbody>
      </table>
    </div>
  );
}
